import type { Granularity } from './api';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export type PeriodOption = { start: string; label: string };

export function localToday(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function monthLabel(month: string): string {
  return MONTHS[Number(month.slice(5, 7)) - 1] ?? month;
}

function yearOf(day: string): number {
  return Number(day.slice(0, 4));
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function addMonths(month: string, n: number): string {
  const total = yearOf(month) * 12 + (Number(month.slice(5, 7)) - 1) + n;
  return `${Math.floor(total / 12)}-${pad((total % 12) + 1)}`;
}

function lastDay(month: string): string {
  const d = new Date(Date.UTC(yearOf(month), Number(month.slice(5, 7)), 0));
  return `${month}-${pad(d.getUTCDate())}`;
}

export function schoolYearStartOf(day: string): string {
  const year = Number(day.slice(5, 7)) >= 7 ? yearOf(day) : yearOf(day) - 1;
  return `${year}-07-01`;
}

export function periodStartOf(granularity: Granularity, day: string): string {
  const month = day.slice(0, 7);
  if (granularity === 'month') return `${month}-01`;
  if (granularity === 'quarter') {
    const m = Number(day.slice(5, 7));
    return `${yearOf(day)}-${pad(Math.floor((m - 1) / 3) * 3 + 1)}-01`;
  }
  return schoolYearStartOf(day);
}

export function periodFullEnd(granularity: Granularity, start: string): string {
  const month = start.slice(0, 7);
  if (granularity === 'month') return lastDay(month);
  if (granularity === 'quarter') return lastDay(addMonths(month, 2));
  return lastDay(addMonths(month, 11));
}

export function shortLabel(granularity: Granularity, start: string, fullEnd: string): string {
  if (granularity === 'month') return monthLabel(start.slice(0, 7));
  if (granularity === 'quarter') return `${monthLabel(start.slice(0, 7))}–${monthLabel(fullEnd.slice(0, 7))}`;
  return `${yearOf(start)}–${String(yearOf(fullEnd)).slice(2)}`;
}

export function periodLabel(granularity: Granularity, start: string, fullEnd: string): string {
  if (granularity === 'year') return shortLabel(granularity, start, fullEnd);
  return `${shortLabel(granularity, start, fullEnd)} ${yearOf(fullEnd)}`;
}

export function rangeLabel(start: string, end: string): string {
  const day = (iso: string) => `${monthLabel(iso.slice(0, 7))} ${Number(iso.slice(8, 10))}`;
  if (yearOf(start) !== yearOf(end)) return `${day(start)}, ${yearOf(start)} – ${day(end)}, ${yearOf(end)}`;
  return `${day(start)} – ${day(end)}, ${yearOf(end)}`;
}

export function schoolYearMonths(schoolYearStart: string, periodEnd: string): string[] {
  const months: string[] = [];
  for (let m = schoolYearStart.slice(0, 7); m <= periodEnd.slice(0, 7); m = addMonths(m, 1)) months.push(m);
  return months;
}

export function periodOptions(granularity: Granularity, today: string): PeriodOption[] {
  const step = granularity === 'month' ? 1 : granularity === 'quarter' ? 3 : 12;
  const earliest = addMonths(schoolYearStartOf(today).slice(0, 7), -12);
  const options: PeriodOption[] = [];
  for (let start = periodStartOf(granularity, today); start.slice(0, 7) >= earliest; start = `${addMonths(start.slice(0, 7), -step)}-01`) {
    options.push({ start, label: periodLabel(granularity, start, periodFullEnd(granularity, start)) });
  }
  return options;
}
